import React from "react";
import { MapPin, Phone, Mail, Globe, Clock, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function OfficeRemoteDetails() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const remoteStates = ["New York", "New Jersey", "Connecticut", "Florida", "Illinois", "Tennessee", "Vermont", "Washington"];
  return <section id="office-remote-details" ref={ref} className={`relative w-full py-24 overflow-hidden bg-background transition-opacity duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-accent/20 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Map */}
          <div className="w-full h-full min-h-[400px] lg:min-h-[600px] relative rounded-2xl overflow-hidden shadow-xl border border-white/20 ring-1 ring-black/5">
            <div className="absolute inset-0 bg-muted/20 z-10 pointer-events-none mix-blend-overlay" />
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.9663095343008!2d-74.00425878428698!3d40.74076684379132!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259bf5c1654f3%3A0xc80f8906e6bd71b9!2s123%20Main%20St%2C%20New%20York%2C%20NY%2010001!5e0!3m2!1sen!2sus!4v1645564756875!5m2!1sen!2sus" width="100%" height="100%" style={{
            border: 0,
            filter: "grayscale(100%) contrast(1.1) opacity(0.85)"
          }} allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Office Location Map" className="absolute inset-0 w-full h-full object-cover" />
            
            {/* Mobile address overlay */}
            <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-4 rounded-xl border border-white/50 shadow-sm lg:hidden">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-full text-primary">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">New York City Office</p>
                  <p className="text-xs text-muted-foreground">315 5th Ave Rm 701, New York City, NY 10016</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-foreground mb-4 tracking-tight">
                Where We Practice
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-xl">
                We bridge the gap between advanced science and accessible care. 
                Visit us in the heart of NYC or connect with us remotely from the comfort of your home.
              </p>
            </div>

            {/* NYC Office Card */}
            <Card className="bg-card/60 backdrop-blur-sm border-white/40 shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-xl">NYC Headquarters</CardTitle>
                </div>
                <CardDescription>
                  Our main office for in-person consultations and treatments.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Sharmat Psychiatric Group</p>
                  <p className="text-muted-foreground">315 5th Ave Rm 701</p>
                  <p className="text-muted-foreground">New York City, NY 10016</p>
                </div>
                
                <div className="flex items-start gap-3 pt-2 border-t border-border/50">
                  <Clock className="w-4 h-4 text-primary mt-1 shrink-0" />
                  <div className="text-sm text-muted-foreground">
                    <p><span className="font-medium text-foreground">Mon–Tue:</span> 11am–7pm</p>
                    <p><span className="font-medium text-foreground">Wed–Thu:</span> 8:30am–4:30pm</p>
                    <p><span className="font-medium text-foreground">Fri:</span> 8:30am–1:30pm</p>
                    <p><span className="font-medium text-foreground">Sat:</span> By appointment</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Remote Care Card */}
            <Card className="bg-card/60 backdrop-blur-sm border-white/40 shadow-sm hover:shadow-md transition-shadow duration-300">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3 mb-1">
                  <div className="p-2 rounded-lg bg-accent text-accent-foreground">
                    <Globe className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-xl">Remote Care Availability</CardTitle>
                </div>
                <CardDescription>
                  Licensed to provide psychiatric services via secure telehealth in these states.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-3">
                  {remoteStates.map((state, i) => <div key={state} data-index={i} className="flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-primary/60" />
                      <span className="text-sm font-medium text-foreground/80">{state}</span>
                    </div>)}
                </div>
              </CardContent>
            </Card>

            {/* Contact CTA */}
            <div className="mt-2 p-6 rounded-xl bg-secondary/30 border border-secondary/50">
              <h3 className="text-lg font-semibold mb-4">Get in Touch</h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild variant="default" className="w-full sm:w-auto shadow-sm">
                  <Link to="tel:+12128047665">
                    <Phone className="w-4 h-4 mr-2" />
                    (212) 804-7665
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}